import axios from 'axios';
import React, { useState } from 'react';
import { API_BASE_URL } from '../constants';
import { useTranslation } from '../contexts/TranslationContext';
import { AVATAR_PALETTE } from '../constants/avatarPalette';
import RegisterProgress from './register/RegisterProgress';
import RegisterProfilePreview from './register/RegisterProfilePreview';
import RegisterStepIdentity from './register/RegisterStepIdentity';
import RegisterStepSports from './register/RegisterStepSports';
import RegisterStepLeague from './register/RegisterStepLeague';
import RegisterStepDescription from './register/RegisterStepDescription';
import RegisterStepAccount from './register/RegisterStepAccount';
import { RegisterFormState, buildProfileTags } from './register/registerTypes';

const TOTAL_STEPS = 5;
const randomAvatarSeed = () => Math.floor(Math.random() * 900000) + 100000;

const Register: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState<RegisterFormState>(() => ({
    fullName: '',
    country: '',
    sports: [],
    league: null,
    description: '',
    avatarColor1: AVATAR_PALETTE[0],
    avatarColor2: AVATAR_PALETTE[1],
    avatarSeed: randomAvatarSeed(),
    email: '',
    password: '',
    repeatPassword: '',
    birthdate: null,
    skipExpert: false,
  }));

  const update = (patch: Partial<RegisterFormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const isExpertProfileComplete = () =>
    form.sports.length === 2 && form.league !== null && form.description.trim().length > 0;

  const handleNotExpertFromStart = () => {
    update({ skipExpert: true });
    setStep(TOTAL_STEPS);
  };

  const handleNotExpertToggle = (skip: boolean) => {
    if (!skip && !isExpertProfileComplete()) {
      update({ skipExpert: false });
      setStep(2);
      return;
    }
    update({ skipExpert: skip });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (form.password !== form.repeatPassword) {
      setError(t.register.passwordMismatch);
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/users/`, {
        email: form.email,
        password: form.password,
        full_name: form.fullName || null,
        country: form.country || null,
        birthdate: form.birthdate ? form.birthdate.toISOString().split('T')[0] : null,
        ...(form.skipExpert ? {} : {
          expert_profile: {
            description: form.description || null,
            tag_1: form.sports[0]?.name || null,
            tag_2: form.sports[1]?.name || null,
            tag_3: form.league?.name || null,
            avatar_color1: form.avatarColor1,
            avatar_color2: form.avatarColor2,
            avatar_seed: form.avatarSeed,
          },
        }),
      });

      setSuccess(t.register.success);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.detail) {
        if (Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail
            .map((d: any) => `${d.loc?.join('.')}: ${d.msg}`)
            .join(', ');
          setError(errorMessages || t.register.validationError);
        } else {
          setError(errorData.detail);
        }
      } else {
        setError(t.register.registrationFailed);
      }
    }
  };

  return (
    <div className="register">
      <RegisterProgress
        step={step}
        totalSteps={TOTAL_STEPS}
        label={t.register.stepIndicator.replace('{n}', String(step))}
      />

      {step === TOTAL_STEPS && form.skipExpert ? (
        <p className="register__skip-summary">{t.register.onlyLookingForPicks}</p>
      ) : (
        <RegisterProfilePreview
          name={form.fullName}
          country={form.country}
          avatarColor1={form.avatarColor1}
          avatarColor2={form.avatarColor2}
          avatarSeed={form.avatarSeed}
          tags={buildProfileTags(form)}
          description={step >= 4 ? form.description : undefined}
        />
      )}

      <div className="register__container">
        {step === 1 && (
          <RegisterStepIdentity
            form={form}
            update={update}
            onContinue={() => setStep(2)}
            onNotExpert={handleNotExpertFromStart}
          />
        )}
        {step === 2 && (
          <RegisterStepSports
            form={form}
            update={update}
            onContinue={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <RegisterStepLeague
            form={form}
            update={update}
            onContinue={() => setStep(4)}
            onBack={() => setStep(2)}
          />
        )}
        {step === 4 && (
          <RegisterStepDescription
            form={form}
            update={update}
            onContinue={() => setStep(5)}
            onBack={() => setStep(3)}
          />
        )}
        {step === 5 && (
          <RegisterStepAccount
            form={form}
            update={update}
            onBack={() => setStep(form.skipExpert ? 1 : 4)}
            onSubmit={handleSubmit}
            onNotExpertToggle={handleNotExpertToggle}
            error={error}
            success={success}
          />
        )}
      </div>
    </div>
  );
};

export default Register;
